import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { Iopd } from '../../opd/opddetails/iopddetails';
import { IPatientInfo } from '../../patient/ipatient-info';
import { IoperationSummary } from '../Iipd.type';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { IpdService } from '../ipd.service';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { ToastrService } from 'ngx-toastr';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, map, Observable, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { NatureOfTreatmentDetailComponent } from '../../settings/nature-of-treatment/nature-of-treatment-detail/nature-of-treatment-detail.component';
import { ExternalEntitiesType, ExternalEntitiesTypeLabels } from 'src/app/common/enums/external-entities-type';
import { ExternalEntitiesComponent } from '../../dialogs/external-entities/external-entities.component';
import { UserDetailComponent } from '../../user/detail/user-detail.component';
import { SuggestedInvestigationPopupComponent } from '../../dialogs/suggested-investigation-popup/suggested-investigation-popup.component';
import { TemplateListComponent } from '../../template/template-list/template-list.component';
import { TemplateType } from 'src/app/common/enums/template-type';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { LoaderComponent } from 'src/app/shared/loader/loader.component';
import { ConvertTo24HourPipe } from 'src/app/pipe/convert-to24-hour.pipe';
import { formatDateOnlyForApi, parseApiDate } from 'src/app/common/function-helper';

@Component({
  selector: 'app-ipd-operation-summary',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
    LoaderComponent
  ],
  providers: [
    ConvertTo24HourPipe,
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './ipd-operation-summary.component.html',
  styleUrl: './ipd-operation-summary.component.scss'
})
export class IpdOperationSummaryComponent implements OnInit, OnDestroy {

  operationForm: FormGroup;
  minDate: Date;
  maxDate: Date = new Date();
  patientDetails: IPatientInfo;
  opdDetails: Iopd;
  isUpdateMode: boolean = false;
  operationSummaryId?: number;
  ipdId?: number;
  readOnly: boolean = false;

  natureOfTreatment: CommonDropDown[] = [];
  filteredNatureOfTreatment$: Observable<CommonDropDown[]>;
  natureOfTreatmentCtrl = new FormControl();
  showAddOptionForNatureOfTreatment = false;
  selectedNatureOfTreatmentId: number | null = null;

  assistantDoctors: CommonDropDown[] = [];
  filteredAssistantDoctors$: Observable<CommonDropDown[]>;
  assistantDoctorCtrl = new FormControl();
  showAddOptionForAssistantDoctor = false;
  selectedAssistantDoctorId: number | null = null;

  anesthetists: CommonDropDown[] = [];
  filteredAnesthetists$: Observable<CommonDropDown[]>;
  anesthetistCtrl = new FormControl();
  showAddOptionForAnesthetist = false;
  selectedAnesthetistId: number | null = null;

  private _unsubscribeAll: Subject<void> = new Subject<void>();
  TemplateType = TemplateType; // Make enum available in template

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<IpdOperationSummaryComponent>,
    @Inject(MAT_DIALOG_DATA) private _data: {
      opdDetails: Iopd,
      patientDetails: IPatientInfo,
      ipdId?: number,
      isUpdateMode?: boolean,
      existingOperationSummary?: IoperationSummary,
      readOnly?: boolean
    },
    private ipdService: IpdService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private toastr: ToastrService,
    private _lookupService: LookupService,
    private dialog: MatDialog,
    private loaderService: LoaderService,
    private convertTo24HourPipe: ConvertTo24HourPipe
  ) {
    this.minDate = new Date(1800, 0, 1);
    this.patientDetails = _data.patientDetails;
    this.opdDetails = _data.opdDetails;
    this.isUpdateMode = _data.isUpdateMode || false;
    this.operationSummaryId = _data.existingOperationSummary?.id;
    this.ipdId = _data.ipdId;
    this.readOnly = _data.readOnly ?? false;
  }

  ngOnInit(): void {
    // Initialize form with default or existing values
    const existingData = this._data.existingOperationSummary;
    // const operationDate = existingData?.operationDate
    //   ? parseApiDate(existingData.operationDate)
    //   : new Date();
    const operationDate = parseApiDate(existingData?.operationDate) ?? new Date();
    const operationTime = existingData?.operationTime
      ? this.convertTimeSpanToTimeInput(existingData.operationTime)
      : this.getCurrentTime();

    this.operationForm = this.fb.group({
      operationDate: [operationDate, [Validators.required]],
      operationTime: [operationTime, [Validators.required]],
      anesthesia: [existingData?.anesthesia || '', [Validators.required]],
      operationNotes: [existingData?.operationNotes || ''],
      historyNotes: [existingData?.historyNotes || ''],
      investigation: [existingData?.investigation || ''],
      natureOfTreatmentId: [existingData?.natureOfTreatmentId || null],
      assistantDoctorId: [existingData?.assistantDoctorId || null],
      treatmentGiven: [existingData?.treatmentGiven || ''],
      anesthetistId: [existingData?.anesthetistId || null]
    });

    // Show loader while loading initial data
    this.loaderService.show();

    // Load all initial data in parallel using forkJoin
    forkJoin({
      natureOfTreatment: this._lookupService.getNatureOfTreatment().pipe(
        takeUntil(this._unsubscribeAll)
      ),
      assistantDoctors: this._lookupService.getAsstDoctors().pipe(
        takeUntil(this._unsubscribeAll)
      ),
      anesthetists: this._lookupService.getExternalEntities(ExternalEntitiesType.Anesthetist).pipe(
        takeUntil(this._unsubscribeAll)
      )
    }).pipe(
      finalize(() => {
        this.loaderService.hide();
      }),
      takeUntil(this._unsubscribeAll)
    ).subscribe({
      next: ({ natureOfTreatment, assistantDoctors, anesthetists }) => {
        // Set nature of treatment data
        this.natureOfTreatment = natureOfTreatment || [];
        if (this.isUpdateMode && existingData?.natureOfTreatmentId) {
          const selected = this.natureOfTreatment.find(not => not.id === existingData.natureOfTreatmentId);
          if (selected) {
            this.natureOfTreatmentCtrl.setValue(selected, { emitEvent: false });
            this.selectedNatureOfTreatmentId = selected.id;
          }
        }

        // Set assistant doctors data
        this.assistantDoctors = assistantDoctors || [];
        if (this.isUpdateMode && existingData?.assistantDoctorId) {
          const selected = this.assistantDoctors.find(doc => doc.id === existingData.assistantDoctorId);
          if (selected) {
            this.assistantDoctorCtrl.setValue(selected);
            this.selectedAssistantDoctorId = selected.id;
          }
        }

        // Set anesthetists data
        this.anesthetists = anesthetists || [];
        if (this.isUpdateMode && existingData?.anesthetistId) {
          const selected = this.anesthetists.find(an => an.id === existingData.anesthetistId);
          if (selected) {
            this.anesthetistCtrl.setValue(selected, { emitEvent: false });
            this.selectedAnesthetistId = selected.id;
          }
        }
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.loaderService.hide();
      }
    });

    // Setup filtered observable for autocomplete - NatureOfTreatment
    this.filteredNatureOfTreatment$ = this.natureOfTreatmentCtrl.valueChanges.pipe(
      startWith(this.natureOfTreatmentCtrl.value || ''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const val = typeof value === 'string' ? value : value?.name || '';
        // If no search value, return the local list as observable
        if (!val) {
          return [this.natureOfTreatment];
        }
        // Otherwise, search from API (this won't update BehaviorSubject)
        return this._lookupService.getNatureOfTreatment(val).pipe(
          tap(list => {
            this.showAddOptionForNatureOfTreatment =
              val && !list.some(x => x.name.toLowerCase() === val.toLowerCase());
          })
        );
      })
    );

    // Setup filtered observable for Assistant Doctors
    this.filteredAssistantDoctors$ = this.assistantDoctorCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      map(value => {
        const val = typeof value === 'string'
          ? value
          : value?.name ?? '';

        const searchValue = val.trim().toLowerCase();

        const filtered = searchValue.length === 0
          ? this.assistantDoctors
          : this.assistantDoctors.filter(d =>
            d.name.toLowerCase().includes(searchValue)
          );

        const hasExactMatch =
          filtered.some(x => x.name.toLowerCase() === searchValue);

        this.showAddOptionForAssistantDoctor =
          searchValue.length > 0 && hasExactMatch === false;

        return filtered;
      })
    );

    // Anesthetists data is now loaded in forkJoin above

    this.filteredAnesthetists$ = this.anesthetistCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const val = typeof value === 'string' ? value : value?.name || '';
        return this._lookupService.getExternalEntities(ExternalEntitiesType.Anesthetist).pipe(
          tap(list => {
            // Update local list when search results come back
            if (!val) {
              this.anesthetists = list;
            }
            this.showAddOptionForAnesthetist = val && !list.some(x => x.name.toLowerCase() === val.toLowerCase());
          }),
          map(list => {
            if (!val) return list;
            return list.filter(a => a.name.toLowerCase().includes(val.toLowerCase()));
          })
        );
      })
    );

    // Handle selection - NatureOfTreatment
    this.natureOfTreatmentCtrl.valueChanges.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        if (value && typeof value === 'object' && value.id) {
          this.selectedNatureOfTreatmentId = value.id;
          this.operationForm.patchValue({ natureOfTreatmentId: value.id });
        } else if (typeof value === 'string' && !value) {
          this.selectedNatureOfTreatmentId = null;
          this.operationForm.patchValue({ natureOfTreatmentId: null });
        }
      });

    // Handle selection - Assistant Doctor
    this.assistantDoctorCtrl.valueChanges.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        if (value && typeof value === 'object' && value.id) {
          this.selectedAssistantDoctorId = value.id;
          this.operationForm.patchValue({ assistantDoctorId: value.id });
        } else if (typeof value === 'string' && !value) {
          this.selectedAssistantDoctorId = null;
          this.operationForm.patchValue({ assistantDoctorId: null });
        }
      });

    // Handle selection - Anesthetist
    this.anesthetistCtrl.valueChanges.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        if (value && typeof value === 'object' && value.id) {
          this.selectedAnesthetistId = value.id;
          this.operationForm.patchValue({ anesthetistId: value.id });
        } else if (typeof value === 'string' && !value) {
          this.selectedAnesthetistId = null;
          this.operationForm.patchValue({ anesthetistId: null });
        }
      });
  }

  displayNatureOfTreatment(natureOfTreatment?: CommonDropDown): string {
    return natureOfTreatment ? natureOfTreatment.name : '';
  }

  onNatureOfTreatmentSelected(value: CommonDropDown) {
    this.selectedNatureOfTreatmentId = value.id;
    this.operationForm.patchValue({ natureOfTreatmentId: value.id });
  }

  displayAssistantDoctor(doctor?: CommonDropDown): string {
    return doctor ? doctor.name : '';
  }

  onAssistantDoctorSelected(value: CommonDropDown) {
    this.selectedAssistantDoctorId = value.id;
    this.operationForm.patchValue({ assistantDoctorId: value.id });
  }

  displayAnesthetist(anesthetist?: CommonDropDown): string {
    return anesthetist ? anesthetist.name : '';
  }

  onAnesthetistSelected(value: CommonDropDown) {
    this.selectedAnesthetistId = value.id;
    this.operationForm.patchValue({ anesthetistId: value.id });
  }

  addNatureOfTreatment(inputValue?: string) {
    const dialogRef = this.dialog.open(NatureOfTreatmentDetailComponent, {
      width: '500px',
      height: 'auto',
      data: {
        natureOfTreatment: inputValue ? { name: inputValue } : null
      }
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        this.refreshNatureOfTreatment(response.result);
      }
    });
  }

  addAnesthetist(inputValue?: string) {
    const dialogRef = this.dialog.open(ExternalEntitiesComponent, {
      width: '500px',
      height: 'auto',
      data: {
        entityType: ExternalEntitiesType.Anesthetist,
        title: ExternalEntitiesTypeLabels[ExternalEntitiesType.Anesthetist],
        prefillName: inputValue || ''
      }
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        this.refreshAnesthetist(response);
      }
    });
  }

  addAssistantDoctor(inputValue?: string) {
    const dialogRef = this.dialog.open(UserDetailComponent, {
      autoFocus: false,
      height: '85vh',
      width: '70vw',
      maxWidth: '70vw',
      disableClose: true,
      data: {
        assistantDoctor: inputValue,
        isEdit: false
      }
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response && response.result) {
        // Check if the created user has AsstDoctor role
        const roleName = response.result.roleName || response.result.role;
        if (roleName === 'AsstDoctor' || roleName === 'asstdoctor') {
          this.refreshAssistantDoctor(response.result);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private getCurrentTime(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  onSaveOperationSummary() {
    if (this.operationForm.invalid) {
      this.operationForm.markAllAsTouched();
      return;
    }

    const formValue = this.operationForm.value;

    const payload: IoperationSummary = {
      opdId: this.opdDetails.id,
      ipdId: this.ipdId || Number(this.opdDetails.ipdId),
      operationDate: formatDateOnlyForApi(formValue.operationDate) || '',
      operationTime: this.convertTo24HourPipe.transform(formValue.operationTime),
      anesthesia: formValue.anesthesia,
      operationNotes: formValue.operationNotes,
      historyNotes: formValue.historyNotes,
      investigation: formValue.investigation,
      natureOfTreatmentId: formValue.natureOfTreatmentId || undefined,
      assistantDoctorId: formValue.assistantDoctorId || undefined,
      treatmentGiven: formValue.treatmentGiven || undefined,
      anesthetistId: formValue.anesthetistId || undefined
    };

    // Create new operation summary
    this.ipdService.createOperationSummary(payload).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const message = this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('BUTTON.BTN_OPERATION_SUMMARY'));
          this.toastr.success(message);
          this.dialogRef.close(true);
        } else {
          const allErrors = Object.values(response.errors || {}).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      },
      error: (error) => {
        const errorMessage = error?.error?.message || 'Error creating operation summary';
        this.toastr.error(errorMessage);
      }
    });
  }

  onUpdateOperationSummary() {
    if (this.operationForm.invalid) {
      this.operationForm.markAllAsTouched();
      return;
    }

    if (!this.operationSummaryId) {
      this.toastr.error('Operation Summary ID is missing');
      return;
    }

    const formValue = this.operationForm.value;

    const payload: IoperationSummary = {
      id: this.operationSummaryId,
      opdId: this.opdDetails.id,
      ipdId: this.ipdId || Number(this.opdDetails.ipdId),
      operationDate: formatDateOnlyForApi(formValue.operationDate) || '',
      operationTime: this.convertTo24HourPipe.transform(formValue.operationTime),
      anesthesia: formValue.anesthesia,
      operationNotes: formValue.operationNotes,
      historyNotes: formValue.historyNotes,
      investigation: formValue.investigation,
      natureOfTreatmentId: formValue.natureOfTreatmentId || undefined,
      assistantDoctorId: formValue.assistantDoctorId || undefined,
      treatmentGiven: formValue.treatmentGiven || undefined,
      anesthetistId: formValue.anesthetistId || undefined
    };

    // Update existing operation summary
    this.ipdService.updateOperationSummary(payload).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const message = this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('BUTTON.BTN_OPERATION_SUMMARY'));
          this.toastr.success(message);
          this.dialogRef.close(true);
        } else {
          const allErrors = Object.values(response.errors || {}).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      },
      error: (error) => {
        const errorMessage = error?.error?.message || 'Error updating operation summary';
        this.toastr.error(errorMessage);
      }
    });
  }

  onPrint() {
    window.print();
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  openSuggestedInvestigationPopup(): void {
    const currentInvestigation = this.operationForm.get('investigation')?.value || '';

    const dialogRef = this.dialog.open(SuggestedInvestigationPopupComponent, {
      height: '75vh',
      minWidth: '55vw',
      disableClose: true,
      data: {
        existingInvestigation: currentInvestigation
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result !== null) {
        // Update investigation form control with selected items
        // Handle empty string case (when all items are unchecked)
        this.operationForm.patchValue({
          investigation: result
        });
      }
    });
  }

  openTemplatePopup(templateType: TemplateType): void {
    // Map template type to form field
    let formFieldName: string | null = null;

    switch (templateType) {
      case TemplateType.TreatmentGiven:
        formFieldName = 'treatmentGiven';
        break;
      case TemplateType.HistoryNotes:
        formFieldName = 'historyNotes';
        break;
      case TemplateType.OperativeNote:
        formFieldName = 'operationNotes';
        break;
      case TemplateType.Anesthesia:
        formFieldName = 'anesthesia';
        break;
    }

    if (!formFieldName) {
      return;
    }

    const templateDialog = this.dialog.open(TemplateListComponent, {
      width: '60vw',
      maxWidth: '70vw',
      height: '60vh',
      maxHeight: '75vh',
      disableClose: true,
      data: {
        templateType: templateType
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result && result.selected && result.template) {
        // Get the current value of the form field
        const currentValue = this.operationForm.get(formFieldName)?.value || '';
        const templateText = result.template.templateDescription || '';

        const parts = [currentValue.trim(), templateText.trim()].filter(Boolean);
        const combinedText = parts.join('\n');

        this.operationForm.get(formFieldName)?.setValue(combinedText);
      }
    });
  }

  private convertTimeSpanToTimeInput(timeSpan: string): string {
    if (!timeSpan) return this.getCurrentTime();

    // Handle TimeSpan format from backend (HH:mm:ss or HH:mm:ss.fffffff)
    const timePart = timeSpan.split('.')[0]; // Remove fractional seconds if present
    const parts = timePart.split(':');

    if (parts.length >= 2) {
      const hours = parts[0];
      const minutes = parts[1];
      // Return in HH:mm format for time input
      return `${hours}:${minutes}`;
    }

    return this.getCurrentTime();
  }


  // Refresh methods
  private refreshNatureOfTreatment(newItem: any): void {
    // Reload data to update BehaviorSubject
    this._lookupService.getNatureOfTreatment()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.natureOfTreatment = data || [];
        // Find the newly added item
        const added = this.natureOfTreatment.find(not =>
          not.id === newItem.id ||
          not.name?.toLowerCase() === newItem.name?.toLowerCase()
        );
        if (added) {
          this.natureOfTreatmentCtrl.setValue(added);
          this.selectedNatureOfTreatmentId = added.id;
          this.operationForm.patchValue({ natureOfTreatmentId: added.id });
        }
      });
  }

  private refreshAssistantDoctor(newItem: any): void {
    // Reload data to update BehaviorSubject
    this._lookupService.getAsstDoctors()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.assistantDoctors = data || [];
        // Find the newly added item by ID or name
        const added = this.assistantDoctors.find(doc =>
          doc.id === newItem.id ||
          (newItem.firstName && newItem.lastName &&
            doc.name?.toLowerCase() === `${newItem.firstName} ${newItem.lastName}`.toLowerCase()) ||
          (newItem.userName && doc.name?.toLowerCase().includes(newItem.userName.toLowerCase()))
        );
        if (added) {
          this.assistantDoctorCtrl.setValue(added);
          this.selectedAssistantDoctorId = added.id;
          this.operationForm.patchValue({ assistantDoctorId: added.id });
        }
      });
  }

  private refreshAnesthetist(newItem: any): void {
    // Reload data to update BehaviorSubject
    this._lookupService.getExternalEntities(ExternalEntitiesType.Anesthetist)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.anesthetists = data || [];
        // Find the newly added item
        const added = this.anesthetists.find(a =>
          a.id === newItem.id ||
          a.name?.toLowerCase() === newItem.name?.toLowerCase()
        );
        if (added) {
          this.anesthetistCtrl.setValue(added);
          this.selectedAnesthetistId = added.id;
          this.operationForm.patchValue({ anesthetistId: added.id });
        }
      });
  }

}
